import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';
import FileSaver from 'file-saver';
import { buildEncodedQueryString } from 'src/common/utils';
import api from '../../../../../app/api';
import { MainDetails } from '../../common/store/types';
import {
  CGTRecord,
  FetchClientReport,
  FetchReportParametersRequest,
  GetReportParameterListRequest,
  InitiateReportRequest,
  InitiateReportRequestModel,
  ReportGeneration,
  SavedParameter,
  SaveReportingParametersRequest,
  TempFileRef,
} from './types';

export enum ClientReportsActionTypes {
  DownloadReport = '@@client/reportsV2/FetchReport',
  InitiateReportGeneration = '@@client/reportsV2/InitiateReportGeneration',
  FetchClientAccount = '@@client/details/reportsV2/Fetch',
  GetCGTRecordsForClient = '@@client/reportsV2/GetCGTRecordsForClient',
  GetReportById = '@@client/reportsV2/GetReportById',
  GetReportParametersList = '@@client/reportsV2/GetReportParametersList',
  GetReportParametersById = '@@client/reportsV2/GetReportParametersById',
  SaveCoverPageFile = '@@client/reportsV2/SaveCoverPageFile',
  SaveAppendixFile = '@@client/reportsV2/SaveAppendixFile',
}

export enum ClientReportsApiEndpoints {
  DownloadReport = '/reporting/DownloadReport',
  initiateReportGeneration = '/reporting/InitiateReportRequest',
  Fetch = '/customers/GetClientMainDetails',
  getReportById = '/reporting/GetReportById',
  getCGTRecords = '/customers/GetCGTRecords',
  getReportParametersList = '/reporting/GetReportParametersList',
  getReportParametersById = '/reporting/GetReportParametersById',
  saveCoverPageFile = '/reporting/SaveCoverPageFile',
  saveAppendixFile = '/reporting/SaveAppendixFile',
}

export const saveCoverPageFile = createAsyncThunk(ClientReportsActionTypes.SaveCoverPageFile, async (request: File, { rejectWithValue }) => {
  try {
    const form = new FormData();
    form.append('file', request);

    const response = await api.put<TempFileRef>(`${ClientReportsApiEndpoints.saveCoverPageFile}`, form);
    return response.data;
  } catch (error) {
    return rejectWithValue({ message: 'Could not save cover page file at this time.', variant: 'error' });
  }
});

export const saveAppendixFile = createAsyncThunk(ClientReportsActionTypes.SaveAppendixFile, async (request: File, { rejectWithValue }) => {
  try {
    const form = new FormData();
    form.append('file', request);

    const response = await api.put<TempFileRef>(`${ClientReportsApiEndpoints.saveAppendixFile}`, form);
    return response.data;
  } catch (error) {
    return rejectWithValue({ message: 'Could not save appendix file at this time.', variant: 'error' });
  }
});

export const downloadReport = createAsyncThunk(ClientReportsActionTypes.DownloadReport, async (request: FetchClientReport) => {
  const options: AxiosRequestConfig = {
    responseType: 'blob',
  };
  const queryString = buildEncodedQueryString({
    key: request.key,
    clientId: request.clientId,
  });
  const response = await api.get<BlobPart>(`${ClientReportsApiEndpoints.DownloadReport}${queryString}`, options);
  const contentType = response.headers['content-type'];
  let extension = contentType.split('/')[1];
  if (contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    extension = 'xlsx';
  }
  const name = request.extension ? `${request.downloadFileName}.${extension}` : `${request.downloadFileName}`;
  const data = new Blob([response.data], { type: contentType });
  FileSaver.saveAs(data, name, { autoBom: false });
  return 'success';
});

export const initiateReportGeneration = createAsyncThunk(
  ClientReportsActionTypes.InitiateReportGeneration,
  async (request: InitiateReportRequest) => {
    const response = await api.post<InitiateReportRequestModel>(`${ClientReportsApiEndpoints.initiateReportGeneration}`, request);
    return response.data;
  }
);

export const getReportStatus = createAsyncThunk(ClientReportsActionTypes.GetReportById, async (reportGeneration: ReportGeneration) => {
  const queryString = buildEncodedQueryString({
    requestId: reportGeneration.requestId,
    clientId: reportGeneration.clientId,
  });
  const response = await api.get<ReportGeneration>(`${ClientReportsApiEndpoints.getReportById}${queryString}`);
  return response.data;
});

export const fetchReportParametersList = createAsyncThunk(
  ClientReportsActionTypes.GetReportParametersList,
  async (request: GetReportParameterListRequest) => {
    const queryString = buildEncodedQueryString({
      afslId: request.afslId,
      kind: request.kind,
    });
    const response = await api.get<SavedParameter[]>(`${ClientReportsApiEndpoints.getReportParametersList}${queryString}`);
    return response.data;
  }
);

export const getReportParametersById = createAsyncThunk(
  ClientReportsActionTypes.GetReportParametersById,
  async (request: FetchReportParametersRequest) => {
    const queryString = buildEncodedQueryString({
      id: request.id,
    });
    const response = await api.get<SaveReportingParametersRequest>(`${ClientReportsApiEndpoints.getReportParametersById}${queryString}`);
    return response.data;
  }
);

export const fetchClientAccount = createAsyncThunk(ClientReportsActionTypes.FetchClientAccount, async (clientId: number) => {
  const queryString = buildEncodedQueryString({
    clientId,
  });
  const response = await api.get<MainDetails>(`${ClientReportsApiEndpoints.Fetch}${queryString}`);
  return response.data;
});

export const getCGTRecordsForClient = createAsyncThunk(ClientReportsActionTypes.GetCGTRecordsForClient, async (clientId: number) => {
  const queryString = buildEncodedQueryString({
    clientId,
  });
  const response = await api.get<CGTRecord[]>(`${ClientReportsApiEndpoints.getCGTRecords}${queryString}`);
  return response.data;
});
